import { useState } from 'react';
import type { FormEvent, PointerEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { login } from '../lib/api';
import { useEmailField } from '../hooks/useEmailValidation';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { connectionErrorMessage, getErrorStatus } from './authErrors';
import { GithubSignInButton } from './GithubSignInButton';
import { GoogleSignInButton } from './GoogleSignInButton';
import { oauthErrorMessage } from './oauthErrors';

function readOauthError(): string | null {
  const params = new URLSearchParams(window.location.search);
  return oauthErrorMessage(params.get('oauth_error'));
}

const LOGIN_EMAIL_ERROR_ID = 'login-email-error';
const LOGIN_PASSWORD_ERROR_ID = 'login-password-error';

function preserveSwitchClick(e: PointerEvent<HTMLButtonElement>) {
  e.preventDefault();
}

/** The paths whose ONLY purpose is the sign-in step. Everywhere else, AuthGate renders
 *  the sign-in form INLINE over the page the operator actually asked for. */
function isDedicatedAuthPath(pathname: string): boolean {
  return pathname === '/login' || pathname === '/signup';
}

function safeReturnTo(): string {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('return_to');
  if (!raw) {
    // T-0306 — no explicit return_to. AuthGate renders this form INLINE, OVER the page the
    // operator deep-linked to (`/org?mesh=X`, `?preview=1`, `/app`, `/schedules`, `/cli-auth`),
    // so the intent lives in the CURRENT url. Handing off to '/' dropped it — the canvas-first
    // landing then bounced to `/org` with no param and opened the account-default org. Instead,
    // STAY on the current path+query: AuthGate re-renders the gated page in place once the `me`
    // check passes, and the deep link survives the round-trip. Returning EXACTLY
    // `pathname + search` means the redirect guard below fires no navigation at all, so React
    // state is preserved rather than blown away by a full reload. Only the dedicated auth paths,
    // which have nothing to stay on, still hand off to '/'.
    return isDedicatedAuthPath(window.location.pathname)
      ? '/'
      : window.location.pathname + window.location.search;
  }
  // Only a same-origin ABSOLUTE PATH may be honored. Two gates, defense-in-depth:
  //
  // 1) The value must begin with a single '/', and its SECOND character must not
  //    start an authority. Rejecting only '//' (the old guard) missed '/\evil.com':
  //    it starts with '/' and not '//', so it passed — but browsers normalize '\'
  //    to '/' in special-scheme URLs, so `location.href = '/\evil.com'` navigates
  //    to https://evil.com (open redirect / phishing pivot, T-0040). Reject a
  //    second char of '/' OR '\'.
  if (!raw.startsWith('/') || raw[1] === '/' || raw[1] === '\\') return '/';
  // 2) Backstop: resolve against the current origin and require the origin to
  //    match — catches any other normalization trick (control chars, encoded
  //    authorities the parser rewrites). Return ONLY the resolved same-origin
  //    path (never a full URL), so the redirect can't leave the origin.
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return '/';
    return url.pathname + url.search + url.hash;
  } catch {
    return '/';
  }
}

interface LoginFormProps {
  onSwitchToSignup?: () => void;
}

export function LoginForm({ onSwitchToSignup }: LoginFormProps = {}) {
  useDocumentTitle('Sign in');

  const emailField = useEmailField('');
  const [password, setPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      const target = safeReturnTo();
      if (target !== window.location.pathname + window.location.search) {
        window.location.href = target;
      }
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    emailField.onBlur();
    setPasswordTouched(true);
    if (!emailField.isValid || !password) return;
    mutation.mutate({ email: emailField.value.trim(), password });
  }

  const error = mutation.error;
  const emailError =
    emailField.error ??
    (emailField.touched && emailField.value.trim().length === 0
      ? 'Enter your email address.'
      : null);
  const passwordError =
    passwordTouched && password.length === 0 ? 'Enter your password.' : null;
  const canSubmit = emailField.isValid && password.length > 0;
  // Surface a GitHub-callback failure hint (?oauth_error=…) if we bounced back
  // here from the OAuth leg.
  const oauthError = readOauthError();

  return (
    <form
      className="login-form"
      onSubmit={handleSubmit}
      aria-label="Sign in"
      noValidate
    >
      <h2>Sign in</h2>
      <p className="login-form-subtitle">
        Welcome back to Tizok — the operator console for your AI coworkers.
      </p>

      {oauthError && (
        <div className="login-form-error" role="alert" aria-live="assertive">
          {oauthError}
        </div>
      )}

      <div className="oauth-section">
        <GithubSignInButton label="Continue with GitHub" />
        <GoogleSignInButton label="Continue with Google" />
        <div className="oauth-divider">or</div>
      </div>

      {error && (
        <div className="login-form-error" role="alert" aria-live="assertive">
          {loginErrorMessage(error)}
        </div>
      )}

      <label className="login-form-field">
        <span>Email</span>
        <input
          autoFocus
          type="email"
          name="email"
          autoComplete="email"
          required
          value={emailField.value}
          onChange={(e) => {
            emailField.setValue(e.target.value);
            if (mutation.isError) mutation.reset();
          }}
          onBlur={emailField.onBlur}
          disabled={mutation.isPending}
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? LOGIN_EMAIL_ERROR_ID : undefined}
        />
        {emailError && (
          <span
            id={LOGIN_EMAIL_ERROR_ID}
            className="login-form-field-error"
            aria-live="polite"
          >
            {emailError}
          </span>
        )}
      </label>

      <div className="login-form-field">
        <span>
          <label htmlFor="login-password">Password</label>
        </span>
        <div className="login-form-password-wrapper">
          <input
            id="login-password"
            type={passwordVisible ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (mutation.isError) mutation.reset();
            }}
            onBlur={() => setPasswordTouched(true)}
            disabled={mutation.isPending}
            aria-invalid={passwordError ? true : undefined}
            aria-describedby={
              passwordError ? LOGIN_PASSWORD_ERROR_ID : undefined
            }
          />
          <button
            type="button"
            className="login-form-password-toggle"
            aria-label={passwordVisible ? 'Hide password' : 'Show password'}
            aria-pressed={passwordVisible}
            onPointerDown={preserveSwitchClick}
            onClick={() => setPasswordVisible((v) => !v)}
            disabled={mutation.isPending}
          >
            {passwordVisible ? 'Hide' : 'Show'}
          </button>
        </div>
        {passwordError && (
          <span
            id={LOGIN_PASSWORD_ERROR_ID}
            className="login-form-field-error"
            aria-live="polite"
          >
            {passwordError}
          </span>
        )}
      </div>

      <button
        type="submit"
        className="login-form-submit"
        disabled={mutation.isPending || !canSubmit}
      >
        {mutation.isPending && (
          <span className="button-spinner" aria-hidden="true" />
        )}
        {mutation.isPending ? 'Signing in…' : 'Sign in'}
      </button>

      <div className="login-form-links">
        <a href="/forgot-password">Forgot password?</a>
        <span aria-hidden="true">·</span>
        {onSwitchToSignup ? (
          <button
            type="button"
            className="login-form-link-button"
            onPointerDown={preserveSwitchClick}
            onClick={onSwitchToSignup}
          >
            Create account
          </button>
        ) : (
          <a href="/signup">Create account</a>
        )}
      </div>

      {/*
        TODO(#111): copy caveat — under the desktop daemon-proxy split this is NOT
        literally true. `tizok login` stores the operator BISCUIT (Bearer); the
        in-app Sign-in stores the `tizok_session` COOKIE (held by atlasd). They are
        distinct credentials (same keychain service, different account). Convergence
        is the §6 agent-auth bearer swap. Copy left as-is pending an Erick decision;
        tracked in FOLLOWUPS_111.md.
      */}
      <p className="login-form-cli-hint">
        Or run <code>tizok login</code> in your terminal — both share the same
        session cookie.
      </p>
    </form>
  );
}

function loginErrorMessage(error: unknown): string {
  const status = getErrorStatus(error);
  if (status === 401) {
    return 'Email or password is incorrect. Check for typos or reset your password.';
  }
  if (status === 429) {
    return 'Too many sign-in attempts. Wait a few minutes, then try again.';
  }
  if (status === 403) {
    return "This account can't sign in right now. Contact support if you think this is a mistake.";
  }
  if (status === null) return connectionErrorMessage();
  if (status >= 500) {
    return "We couldn't sign you in because the server had a problem. Try again in a minute.";
  }
  return "We couldn't sign you in. Try again in a minute.";
}
